import { useId, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  ConfirmActionModal,
  Form,
} from '@/shared/components';
import { useDraftEditor } from '../../hooks/useDraftEditor';
import { useTemplatePreview } from '../../hooks/useTemplatePreview';
import { useUnknownPlaceholders } from '../../hooks/useUnknownPlaceholders';
import type { TemplateVersion } from '../../types/documentTemplates.types';
import { hasBodyContent } from '../../utils/bodyContent';
import { publishBlockedReason, publishReadiness } from '../../utils/publishReadiness';
import { draftSubtitle } from '../../utils/versionLabels';
import { DiscardDraftAction } from '../DiscardDraftAction';
import { PublishDraftAction } from '../PublishDraftAction';
import { TemplateBodyFields } from '../TemplateBodyFields';
import { TemplatePreviewModal } from '../TemplatePreviewModal';
import { VariableReferencePanel } from '../VariableReferencePanel';
import { PublishChecklist } from './PublishChecklist';

interface Props {
  templateId: string;
  draft: TemplateVersion;
  canEdit: boolean;
  supersedesVersionNumber: number | null;
}

export function DraftEditor({ templateId, draft, canEdit, supersedesVersionNumber }: Props) {
  const editor = useDraftEditor(templateId, draft);
  const { form, blocker } = editor;
  const [bodyHtml, changeNote] = useWatch({ control: form.control, name: ['bodyHtml', 'changeNote'] });
  const unknownKeys = useUnknownPlaceholders(bodyHtml);
  const preview = useTemplatePreview(templateId);
  const hasContent = hasBodyContent(bodyHtml);
  const previewHintId = useId();
  const readiness = publishReadiness({
    hasContent,
    isDirty: editor.isDirty,
    hasConflict: editor.hasConflict,
    unknownVariableCount: unknownKeys?.length ?? null,
    changeNote,
  });
  const insertRef = useRef<((token: string) => void) | null>(null);
  const insertVariable = (token: string) => insertRef.current?.(token);

  return (
    <div className="grid items-start gap-6 xl:grid-cols-3">
      <Card className="min-w-0 xl:col-span-2">
        <Form onSubmit={editor.save}>
          <CardHeader className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Draft v{draft.versionNumber}</CardTitle>
              <p className="mt-0.5 text-sm text-mp-muted">
                {draftSubtitle(draft, editor.revision, supersedesVersionNumber)}
              </p>
            </div>
            {editor.isDirty && <Badge variant="warning">Unsaved changes</Badge>}
          </CardHeader>

          <CardContent className="space-y-4 py-5">
            {editor.hasConflict && (
              <Alert variant="warning">
                <span>
                  This draft was changed elsewhere since you loaded it. Your text is kept below. Load the latest draft
                  to continue from the saved version, which replaces what is in the editor.
                </span>
                <Button size="sm" variant="neutral" appearance="outline" type="button" onClick={editor.reloadLatest} disabled={editor.isReloading}>
                  Load latest draft
                </Button>
              </Alert>
            )}
            {editor.saveError && (
              <Alert variant="error">
                <span>{editor.saveError}</span>
              </Alert>
            )}
            <TemplateBodyFields control={form.control} bodyHtml={bodyHtml} disabled={!canEdit} insertRef={insertRef} />
          </CardContent>

          <CardFooter className="flex-wrap">
            <DiscardDraftAction templateId={templateId} draftVersionNumber={draft.versionNumber} className="mr-auto" />
            {!hasContent && (
              <p id={previewHintId} className="text-xs text-mp-muted">
                Write the body to preview it.
              </p>
            )}
            <Button
              type="button"
              variant="neutral"
              appearance="outline"
              size="sm"
              ariaDisabled={!hasContent}
              ariaDescribedBy={hasContent ? undefined : previewHintId}
              onClick={() => preview.open({ bodyHtml })}
            >
              Preview
            </Button>
            {canEdit && (
              <Button type="submit" variant="neutral" appearance="outline" size="sm" disabled={editor.isSaving || editor.hasConflict}>
                {editor.isSaving ? 'Saving...' : 'Save draft'}
              </Button>
            )}
            <PublishDraftAction
              templateId={templateId}
              draftVersionNumber={draft.versionNumber}
              revision={editor.revision}
              supersedesVersionNumber={supersedesVersionNumber}
              blockedReason={publishBlockedReason(readiness)}
            />
          </CardFooter>
        </Form>
      </Card>

      <div className="min-w-0 xl:self-stretch">
        <div className="flex flex-col gap-4 xl:sticky xl:top-5 xl:max-h-[calc(100dvh-var(--env-banner-height)-7.5rem)]">
          <PublishChecklist items={readiness} />
          {canEdit && <VariableReferencePanel onInsert={insertVariable} />}
        </div>
      </div>

      <TemplatePreviewModal
        isOpen={preview.isOpen}
        onClose={preview.close}
        isLoading={preview.isLoading}
        error={preview.error}
        preview={preview.preview}
      />
      <ConfirmActionModal
        isOpen={blocker.state === 'blocked'}
        title="Discard unsaved changes?"
        message="You have unsaved changes to this draft. Leaving this page will lose them."
        confirmLabel="Leave page"
        cancelLabel="Stay"
        confirmVariant="warning"
        onConfirm={() => blocker.proceed?.()}
        onClose={() => blocker.reset?.()}
      />
    </div>
  );
}
